// src/api/modulo.js - API para manejo de módulos (con jerarquía)
import { apiFetch } from './index.js';


// ==================== FUNCIONES AUXILIARES (NO API) ====================

const normalizarModulo = (m) => {
  if (!m) return null;

  const getProp = (obj, name) => {
    if (!obj) return undefined;
    const key = Object.keys(obj).find(
      k => k.toLowerCase().replace(/_/g, '') === name.toLowerCase().replace(/_/g, '')
    );
    return key ? obj[key] : undefined;
  };

  return {
    ...m,
    idModulo:    m.idModulo ?? m.IdModulo ?? getProp(m, 'idModulo') ?? getProp(m, 'id'),
    idPadre:     m.idPadre ?? m.IdPadre ?? getProp(m, 'id_padre') ?? null,
    nombre:      m.nombre ?? m.Nombre ?? getProp(m, 'nombre') ?? '',
    descripcion: m.descripcion ?? m.Descripcion ?? getProp(m, 'descripcion') ?? '',
    icono:       m.icono ?? m.Icono ?? getProp(m, 'icono') ?? 'mdi:folder-outline',
    ruta:        m.ruta ?? m.Ruta ?? getProp(m, 'ruta') ?? '',
    estado:      m.estado ?? m.Estado ?? getProp(m, 'estado') ?? 'activo',
    tipo:        m.tipo ?? m.Tipo ?? getProp(m, 'tipo') ?? 'modulo',
    orden:       m.orden ?? m.Orden ?? getProp(m, 'orden') ?? 0,
    fechaCreacion: m.fecha_creacion ?? m.fechaCreacion ?? m.FechaCreacion ?? getProp(m, 'fechaCreacion')
  };
};

const normalizarLista = (response) => {
  let lista = response;
  if (response && response.data && Array.isArray(response.data)) lista = response.data;
  if (!Array.isArray(lista)) return [];
  return lista.map(normalizarModulo).filter(Boolean);
};

// ==================== FUNCIONES API ====================

export const obtenerModulos = async (estado = 'all') => {
  try {
    console.log('📦 Obteniendo módulos...', { estado });
    let endpoint = '/Modulo';

    if (estado === 'activos') {
      endpoint = '/Modulo/activos';
    } else if (estado === 'inactivos') {
      const response = await apiFetch('/Modulo');
      const inactivos = normalizarLista(response).filter(m => m.estado?.toLowerCase() === 'inactivo');
      return { success: true, data: inactivos, count: inactivos.length };
    }

    const response = await apiFetch(endpoint);
    const modulos = normalizarLista(response);
    return { success: true, data: modulos, count: modulos.length };
  } catch (error) {
    console.error('❌ Error obteniendo módulos:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

export const obtenerEstructuraMenu = async () => {
  try {
    const response = await apiFetch('/Modulo/estructura-menu');
    return { success: true, data: response };
  } catch (error) {
    return { success: false, error: error.message, data: [] };
  }
};

export const obtenerModulosPorRol = async (idRol) => {
  try {
    if (!idRol) throw new Error('ID de rol es requerido');
    const response = await apiFetch(`/Modulo/por-rol/${idRol}`);
    const modulos = normalizarLista(response);
    return { success: true, data: modulos, count: modulos.length };
  } catch (error) {
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

export const obtenerModuloPorId = async (id) => {
  try {
    if (!id) throw new Error('ID de módulo es requerido');
    const response = await apiFetch(`/Modulo/${id}`);
    return { success: true, data: normalizarModulo(response) };
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

export const obtenerModuloConPermisos = async (id) => {
  try {
    if (!id) throw new Error('ID de módulo es requerido');
    const response = await apiFetch(`/Modulo/${id}/con-permisos`);
    return { success: true, data: response };
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

export const verificarModuloTienePermisos = async (id) => {
  try {
    if (!id) throw new Error('ID de módulo es requerido');
    const response = await apiFetch(`/Modulo/${id}/tiene-permisos`);
    const tienePermisos = typeof response === 'boolean' ? response : response.tienePermisos || false;
    return { success: true, tienePermisos };
  } catch (error) {
    return { success: false, error: error.message, tienePermisos: false };
  }
};

export const verificarModuloExiste = async (id) => {
  try {
    if (!id) throw new Error('ID es requerido');
    const response = await apiFetch(`/Modulo/${id}/exists`);
    const existe = typeof response === 'boolean' ? response : response.existe || false;
    return { success: true, existe };
  } catch (error) {
    return { success: false, error: error.message, existe: false };
  }
};

export const verificarNombreModulo = async (nombre) => {
  try {
    if (!nombre || nombre.trim() === '') throw new Error('Nombre es requerido');
    const response = await apiFetch(`/Modulo/validar-nombre?nombre=${encodeURIComponent(nombre)}`);
    const existe = typeof response === 'boolean' ? response : response.existe || false;
    return { success: true, existe, disponible: !existe };
  } catch (error) {
    return { success: false, error: error.message, existe: false, disponible: false };
  }
};

export const obtenerIconosDisponibles = async () => {
  try {
    const response = await apiFetch('/Modulo/iconos-disponibles');
    return { success: true, data: response };
  } catch (error) {
    return { success: false, error: error.message, data: [] };
  }
};

// =====================================================
// CREAR MÓDULO
// =====================================================
export const crearModulo = async (moduloData) => {
  try {
    if (!moduloData.nombre?.trim()) {
      throw new Error('El nombre es requerido');
    }

    const tipoFinal = moduloData.idPadre ? 'submodulo' : 'modulo';

    console.log('➕ Creando nuevo módulo:', moduloData);

    const response = await apiFetch('/Modulo', {
      method: 'POST',
      body: JSON.stringify({
        idPadre:     moduloData.idPadre || null,       // ⭐ CORREGIDO: camelCase
        nombre:      moduloData.nombre.trim(),
        descripcion: moduloData.descripcion || '',
        icono:       moduloData.icono || 'mdi:folder-outline',
        ruta:        moduloData.ruta?.trim() || '',
        estado:      moduloData.estado || 'Activo',
        tipo:        tipoFinal,
        orden:       moduloData.orden ?? 0
      })
    });

    return {
      success: true,
      data: normalizarModulo(response),
      message: 'Módulo creado exitosamente'
    };

  } catch (error) {
    console.error('❌ Error creando módulo:', error);
    return { success: false, error: error.message, data: null };
  }
};

// =====================================================
// ACTUALIZAR MÓDULO
// =====================================================
export const actualizarModulo = async (id, moduloData) => {
  try {
    if (!id) throw new Error('ID de módulo es requerido');

    const tipoFinal = moduloData.idPadre ? 'submodulo' : 'modulo';

    console.log(`✏️ Actualizando módulo ID: ${id}`, moduloData);

    const response = await apiFetch(`/Modulo/${id}`, {
      method: 'PUT',
      body: JSON.stringify({
        idModulo:    Number(id),
        idPadre:     moduloData.idPadre || null,       // ✅ ya estaba bien (camelCase)
        nombre:      moduloData.nombre?.trim(),
        descripcion: moduloData.descripcion || '',
        icono:       moduloData.icono || 'mdi:folder-outline',
        ruta:        moduloData.ruta?.trim() || '',
        estado:      moduloData.estado || 'Activo',
        tipo:        tipoFinal,
        orden:       moduloData.orden ?? 0
      })
    });

    return {
      success: true,
      data: normalizarModulo(response),
      message: 'Módulo actualizado exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error actualizando módulo ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

export const eliminarModulo = async (id) => {
  try {
    if (!id) throw new Error('ID de módulo es requerido');
    const response = await apiFetch(`/Modulo/${id}`, { method: 'DELETE' });
    return { success: true, data: response, message: 'Módulo eliminado exitosamente' };
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

export const cambiarEstadoModulo = async (data) => {
  try {
    const { idModulo, estado } = data;
    if (!idModulo || !estado) throw new Error('ID y estado son requeridos');
    const response = await apiFetch('/Modulo/cambiar-estado', {
      method: 'PATCH',
      body: JSON.stringify({ idModulo, estado })
    });
    return { success: true, data: response, message: 'Estado actualizado' };
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

// ==================== FUNCIONES AUXILIARES ====================

export const formatearFechaModulo = (fechaISO, completo = false) => {
  if (!fechaISO) return 'N/A';
  try {
    const fecha = new Date(fechaISO);
    const opciones = { year: 'numeric', month: 'short', day: 'numeric' };
    if (completo) {
      opciones.hour = '2-digit';
      opciones.minute = '2-digit';
    }
    return fecha.toLocaleDateString('es-ES', opciones);
  } catch { return fechaISO; }
};

export const convertirEstadoATexto = (estado) => {
  if (!estado) return 'Sin estado';
  return estado.charAt(0).toUpperCase() + estado.slice(1).toLowerCase();
};

export const convertirEstadoABoolean = (estado) => {
  return estado?.toLowerCase() === 'activo';
};

export const buscarModulos = (modulos, termino) => {
  if (!termino || termino.trim() === '') return modulos;
  const t = termino.toLowerCase();
  return modulos.filter(m =>
    m.nombre?.toLowerCase().includes(t) ||
    m.descripcion?.toLowerCase().includes(t) ||
    m.ruta?.toLowerCase().includes(t) ||
    m.icono?.toLowerCase().includes(t)
  );
};

export const filtrarModulosPorEstado = (modulos, estado) => {
  if (estado === 'all') return modulos;
  if (estado === 'activos') return modulos.filter(m => m.estado?.toLowerCase() === 'activo');
  if (estado === 'inactivos') return modulos.filter(m => m.estado?.toLowerCase() === 'inactivo');
  return modulos;
};

export const filtrarModulosPorTipo = (modulos, tipo) => {
  if (tipo === 'all') return modulos;
  if (tipo === 'modulo') return modulos.filter(m => !m.idPadre);
  if (tipo === 'submodulo') return modulos.filter(m => m.idPadre);
  return modulos;
};

export const obtenerEstadisticas = (modulos) => {
  const activos = modulos.filter(m => m.estado?.toLowerCase() === 'activo').length;
  const inactivos = modulos.filter(m => m.estado?.toLowerCase() === 'inactivo').length;
  const conIconos = modulos.filter(m => m.icono && m.icono !== 'mdi:folder-outline').length;
  const raices = modulos.filter(m => !m.idPadre).length;
  const submodulos = modulos.filter(m => m.idPadre).length;

  return {
    total: modulos.length,
    activos,
    inactivos,
    conIconos,
    raices,
    submodulos,
    porcentajeActivos: modulos.length > 0 ? Math.round((activos / modulos.length) * 100) : 0,
    porcentajeInactivos: modulos.length > 0 ? Math.round((inactivos / modulos.length) * 100) : 0
  };
};

export const ordenarModulos = (modulos, criterio = 'nombre') => {
  const copy = [...modulos];

  switch (criterio) {
    case 'nombre':
      return copy.sort((a, b) => (a.nombre || '').toLowerCase().localeCompare((b.nombre || '').toLowerCase()));
    case 'nombre_desc':
      return copy.sort((a, b) => (b.nombre || '').toLowerCase().localeCompare((a.nombre || '').toLowerCase()));
    case 'ruta':
      return copy.sort((a, b) => (a.ruta || '').localeCompare(b.ruta || ''));
    case 'orden':
      return copy.sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
    case 'fechaCreacion_desc':
      return copy.sort((a, b) => new Date(b.fechaCreacion || 0) - new Date(a.fechaCreacion || 0));
    default:
      return copy;
  }
};

export const obtenerIconoSeguro = (icono) => {
  if (!icono) return 'mdi:folder-outline';
  return icono;
};

export const agruparModulosPorEstado = (modulos) => {
  return {
    activos: modulos.filter(m => m.estado?.toLowerCase() === 'activo'),
    inactivos: modulos.filter(m => m.estado?.toLowerCase() === 'inactivo')
  };
};

export const agruparModulosPorPadre = (modulos) => {
  const raices = modulos.filter(m => !m.idPadre);
  return raices.map(raiz => ({
    ...raiz,
    hijos: modulos.filter(m => m.idPadre === raiz.idModulo)
  }));
};

// ==================== EXPORT DEFAULT ====================

export default {
  // API
  obtenerModulos,
  obtenerEstructuraMenu,
  obtenerModulosPorRol,
  obtenerModuloPorId,
  obtenerModuloConPermisos,
  verificarModuloTienePermisos,
  verificarModuloExiste,
  verificarNombreModulo,
  obtenerIconosDisponibles,
  crearModulo,
  actualizarModulo,
  eliminarModulo,
  cambiarEstadoModulo,

  // Auxiliares
  formatearFechaModulo,
  convertirEstadoATexto,
  convertirEstadoABoolean,
  buscarModulos,
  filtrarModulosPorEstado,
  filtrarModulosPorTipo,
  obtenerEstadisticas,
  ordenarModulos,
  obtenerIconoSeguro,
  agruparModulosPorEstado,
  agruparModulosPorPadre
};